import { lazy, useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Outlet, useLocation } from 'react-router'

import { BundleScanSheet } from '../common/bundle-scan-sheet/bundle-scan-sheet'
import { ScanWarningOverlay } from '../common/scan-warning-overlay/scan-warning-overlay'
import { SkidDetailsSheet } from '../common/skid-details-sheet/skid-details-sheet'
import { StickersPopup } from '../stickers/stickers-popup'

import { AppSidebar } from './app-sidebar'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Toaster } from '@/components/ui/sonner'
import { useGlobalScanner } from '@/hooks/use-global-scanner'
import { persistPackagingParam } from '@/hooks/use-packaging-url-persistence'
import { useSkidPanelStore } from '@/store/skid-panel'

const ErrorPage = lazy(() => import('@/pages/error-page'))

interface LayoutProps {
  withSidebar?: boolean
}

// Packaging-only state (bulkScan, bundleScanActive, persisted ?detail=) lives
// in sessionStorage and the zustand store so it can survive AHK Ctrl+L
// reloads. Without an explicit clear it would also survive the operator
// navigating to a non-packaging page (e.g. /scheduling), then come back to
// hijack the next bundle scan. Drop it on transitions out of the packaging
// surface; the surface includes the order-detail page (/order/:id) so
// intra-packaging navigation keeps the session alive.
function isPackagingPath(pathname: string): boolean {
  return (
    pathname === '/packaging' ||
    pathname.startsWith('/packaging/') ||
    pathname.startsWith('/order/') ||
    pathname === '/multi-order-skids'
  )
}

export const Layout = ({ withSidebar = true }: LayoutProps) => {
  useGlobalScanner()
  const { pathname } = useLocation()
  useEffect(() => {
    if (isPackagingPath(pathname)) return
    useSkidPanelStore.getState().setBulkScan(null)
    useSkidPanelStore.getState().setBundleScanActive(null)
    persistPackagingParam('detail', null)
  }, [pathname])

  return (
    <ErrorBoundary
      onReset={() => window.location.reload()}
      FallbackComponent={ErrorPage}
    >
      {withSidebar ? (
        <div className='flex'>
          <AppSidebar />
          <main className='min-w-0 flex-1 px-[var(--main-content-padding)] pt-[var(--main-content-padding)]'>
            {/* Below md the sidebar turns into an off-canvas sheet with no way to open it. */}
            <SidebarTrigger className='mb-2 size-9 md:hidden' />
            <Outlet />
          </main>
        </div>
      ) : (
        <main className='h-screen p-[var(--main-content-padding)]'>
          <Outlet />
        </main>
      )}
      <StickersPopup />
      <SkidDetailsSheet />
      <BundleScanSheet />
      <ScanWarningOverlay />
      <Toaster
        richColors
        duration={5000}
      />
    </ErrorBoundary>
  )
}
